import { useState, useEffect, useRef, useCallback } from 'react';
import { Bot, Send, X, Loader2, RotateCcw, AlertTriangle } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function getOrCreateSessionId() {
  let id = localStorage.getItem('lifelink_chatbot_session');
  if (!id) {
    id = 'tri_' + Math.random().toString(36).substring(2, 12) + '_' + Date.now();
    localStorage.setItem('lifelink_chatbot_session', id);
  }
  return id;
}

const WELCOME_MSG = {
  role: 'assistant',
  content: 'Ciao, sono l\'assistente di triage di LIFE LINKE. Sono qui per aiutarti a inquadrare la tua emergenza. Raccontami brevemente cosa sta succedendo. Se c\'è pericolo di vita immediato, chiama subito il 112.'
};

export default function ChatbotTriage() {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([WELCOME_MSG]);
  const [input, setInput] = useState('');
  const [sending, setSending] = useState(false);
  const [sessionId] = useState(getOrCreateSessionId());
  const endRef = useRef(null);

  const token = localStorage.getItem('lifelink_token');
  const headers = token ? { Authorization: `Bearer ${token}` } : {};

  // Open via global event from SOS widget or Home button
  useEffect(() => {
    const handler = () => setOpen(true);
    window.addEventListener('open-chatbot-triage', handler);
    return () => window.removeEventListener('open-chatbot-triage', handler);
  }, []);

  // Load history on open
  const loadHistory = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/chatbot/history/${sessionId}`);
      if (data?.messages?.length) {
        setMessages([WELCOME_MSG, ...data.messages.map(m => ({ role: m.role, content: m.content }))]);
      }
    } catch { /* no-op */ }
  }, [sessionId]);

  useEffect(() => {
    if (open) loadHistory();
  }, [open, loadHistory]);

  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, open]);

  const send = async () => {
    const text = input.trim();
    if (!text || sending) return;
    setSending(true);
    setMessages(m => [...m, { role: 'user', content: text }]);
    setInput('');
    try {
      const { data } = await axios.post(`${API}/chatbot/triage`,
        { session_id: sessionId, message: text },
        { headers }
      );
      setMessages(m => [...m, { role: 'assistant', content: data.reply }]);
    } catch (e) {
      toast.error('Chatbot non disponibile');
      setMessages(m => [...m, { role: 'assistant', content: 'Mi dispiace, non riesco a rispondere in questo momento. Per emergenze, chiama il 112.' }]);
    } finally {
      setSending(false);
    }
  };

  const resetSession = async () => {
    try {
      await axios.delete(`${API}/chatbot/history/${sessionId}`);
    } catch { /* no-op */ }
    localStorage.removeItem('lifelink_chatbot_session');
    setMessages([WELCOME_MSG]);
    toast.success('Conversazione resettata');
  };

  const handleKeyDown = (e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); send(); } };

  return (
    <>
      {/* Floating chatbot button - bottom right */}
      {!open && (
        <button
          onClick={() => setOpen(true)}
          data-testid="chatbot-trigger"
          title="Assistente AI Triage"
          className="fixed bottom-4 right-4 z-30 w-14 h-14 bg-[#0A84FF] hover:bg-[#0070E0] text-white shadow-2xl flex items-center justify-center transition-all hover:scale-110 active:scale-95"
          style={{ boxShadow: '0 6px 20px rgba(10,132,255,0.45)' }}
        >
          <Bot size={26} strokeWidth={2.2} />
        </button>
      )}

      {open && (
        <div
          className="fixed inset-0 z-50 bg-black/40 backdrop-blur-sm flex items-end sm:items-center justify-center sm:p-4"
          onClick={() => setOpen(false)}
          data-testid="chatbot-panel"
        >
          <div
            className="bg-white border border-[#B8D4E8] w-full sm:max-w-lg h-[80vh] sm:h-[600px] flex flex-col shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div className="bg-[#0D1B2A] text-white p-3 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 bg-[#0A84FF] flex items-center justify-center">
                  <Bot size={16} strokeWidth={2.2} />
                </div>
                <div>
                  <div className="text-sm font-bold uppercase tracking-widest">Assistente Triage AI</div>
                  <div className="text-[9px] text-[#B8D4E8] uppercase tracking-widest">LIFE LINKE</div>
                </div>
              </div>
              <div className="flex items-center gap-1">
                <button onClick={resetSession} title="Resetta conversazione"
                  className="p-1.5 hover:bg-white/10 transition-all" data-testid="chatbot-reset-btn">
                  <RotateCcw size={14} />
                </button>
                <button onClick={() => setOpen(false)} className="p-1.5 hover:bg-white/10 transition-all" data-testid="chatbot-close-btn">
                  <X size={16} />
                </button>
              </div>
            </div>

            {/* Warning banner */}
            <div className="bg-[#FFF7E6] border-b border-[#FF9F0A]/30 px-3 py-2 flex items-center gap-2">
              <AlertTriangle size={12} className="text-[#FF9F0A] flex-shrink-0" />
              <p className="text-[10px] text-[#0D1B2A]">In caso di pericolo di vita, chiama subito il <a href="tel:112" className="font-bold text-[#FF3B30]">112</a>.</p>
            </div>

            {/* Messages */}
            <div className="flex-1 overflow-y-auto p-4 space-y-3 bg-[#F0F7FC]" data-testid="chatbot-messages">
              {messages.map((m, i) => (
                <div key={i} className={`flex ${m.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                  <div className={`max-w-[85%] px-3 py-2 text-sm leading-relaxed whitespace-pre-wrap ${
                    m.role === 'user'
                      ? 'bg-[#0A84FF] text-white'
                      : 'bg-white border border-[#B8D4E8] text-[#0D1B2A]'
                  }`}>
                    {m.content}
                  </div>
                </div>
              ))}
              {sending && (
                <div className="flex justify-start">
                  <div className="bg-white border border-[#B8D4E8] px-3 py-2 flex items-center gap-2 text-xs text-[#4A6B8A]">
                    <Loader2 size={12} className="animate-spin" />
                    <span>L&apos;assistente sta scrivendo...</span>
                  </div>
                </div>
              )}
              <div ref={endRef} />
            </div>

            {/* Input */}
            <div className="p-3 border-t border-[#B8D4E8] bg-white">
              <div className="flex gap-2">
                <textarea
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  onKeyDown={handleKeyDown}
                  placeholder="Scrivi la tua situazione..."
                  rows={2}
                  disabled={sending}
                  className="flex-1 bg-[#F0F7FC] border border-[#B8D4E8] text-[#0D1B2A] px-3 py-2 focus:border-[#0A84FF] focus:outline-none text-sm resize-none"
                  data-testid="chatbot-input"
                />
                <button
                  onClick={send}
                  disabled={sending || !input.trim()}
                  className="px-4 py-2 bg-[#0A84FF] text-white hover:bg-[#0070E0] transition-all disabled:opacity-40 self-stretch"
                  data-testid="chatbot-send-btn"
                >
                  {sending ? <Loader2 size={16} className="animate-spin" /> : <Send size={16} />}
                </button>
              </div>
              <p className="text-[9px] text-[#6B8AB5] mt-1 uppercase tracking-widest">Powered by Emergent LLM · Triage AI</p>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
